import React, { useState, useEffect } from "react";
import { SlidersHorizontal, RotateCcw } from "lucide-react";

export default function ExperienceRangeSlider({ value, onChange }) {
  // value can be "all", "0-2", "3-5", "6-8", "9+" or custom "min-max" e.g. "2-7"
  const parseRange = (val) => {
    if (!val || val === "all") return [0, 15];
    if (val === "0-2") return [0, 2];
    if (val === "3-5") return [3, 5];
    if (val === "6-8") return [6, 8];
    if (val === "9+") return [9, 15];
    const parts = String(val).split("-");
    if (parts.length === 2) {
      const min = parseInt(parts[0], 10) || 0;
      const max = parseInt(parts[1], 10) || 15;
      return [min, max];
    }
    return [0, 15];
  };

  const [[minExp, maxExp], setRange] = useState(() => parseRange(value));

  useEffect(() => {
    setRange(parseRange(value));
  }, [value]);

  const handleMinChange = (e) => {
    const val = Math.min(Number(e.target.value), maxExp - 1);
    setRange([val, maxExp]);
    emitChange(val, maxExp);
  };

  const handleMaxChange = (e) => {
    const val = Math.max(Number(e.target.value), minExp + 1);
    setRange([minExp, val]);
    emitChange(minExp, val);
  };

  const emitChange = (min, max) => {
    if (min === 0 && max >= 15) {
      onChange("all");
    } else {
      onChange(`${min}-${max}`);
    }
  };

  const isAll = minExp === 0 && maxExp >= 15;
  const displayLabel = isAll
    ? "All Experience (0 - 15+ yrs)"
    : `${minExp} - ${maxExp >= 15 ? "15+ yrs" : `${maxExp} yrs`}`;

  // Track percentage calculations for active fill
  const minPercent = (minExp / 15) * 100;
  const maxPercent = (maxExp / 15) * 100;

  return (
    <div className="flex flex-col gap-2 rounded-2xl border border-border bg-card/70 p-3 shadow-xs">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="h-3.5 w-3.5 text-violet" />
          <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Experience Range:
          </span>
          <span className="rounded-full bg-violet/15 px-2.5 py-0.5 text-xs font-bold text-violet">
            {displayLabel}
          </span>
        </div>

        {!isAll && (
          <button
            type="button"
            onClick={() => {
              setRange([0, 15]);
              onChange("all");
            }}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground transition-colors"
          >
            <RotateCcw className="h-3 w-3" /> Reset
          </button>
        )}
      </div>

      {/* Draggable Dual Range Slider */}
      <div className="relative py-2 px-1">
        {/* Track Background */}
        <div className="relative h-2 w-full rounded-full bg-secondary">
          {/* Active Highlighted Range */}
          <div
            className="absolute h-2 rounded-full bg-gradient-to-r from-electric to-violet"
            style={{
              left: `${minPercent}%`,
              width: `${maxPercent - minPercent}%`,
            }}
          />
        </div>

        {/* Min Thumb Range Input */}
        <input
          type="range"
          min="0"
          max="15"
          step="1"
          value={minExp}
          onChange={handleMinChange}
          aria-label="Minimum experience"
          className="pointer-events-none absolute inset-x-0 top-1.5 h-2 w-full appearance-none bg-transparent accent-violet [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:cursor-grab [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:bg-electric [&::-webkit-slider-thumb]:shadow-md hover:[&::-webkit-slider-thumb]:scale-110 active:[&::-webkit-slider-thumb]:cursor-grabbing [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:cursor-grab [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:bg-electric [&::-moz-range-thumb]:shadow-md"
        />

        {/* Max Thumb Range Input */}
        <input
          type="range"
          min="0"
          max="15"
          step="1"
          value={maxExp}
          onChange={handleMaxChange}
          aria-label="Maximum experience"
          className="pointer-events-none absolute inset-x-0 top-1.5 h-2 w-full appearance-none bg-transparent accent-violet [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:cursor-grab [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:bg-violet [&::-webkit-slider-thumb]:shadow-md hover:[&::-webkit-slider-thumb]:scale-110 active:[&::-webkit-slider-thumb]:cursor-grabbing [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:cursor-grab [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:bg-violet [&::-moz-range-thumb]:shadow-md"
        />
      </div>

      {/* Quick Presets for Instant One-Click Selection */}
      <div className="flex flex-wrap items-center gap-1 pt-1">
        <span className="text-[10px] font-semibold text-muted-foreground mr-1">Presets:</span>
        {[
          { id: "all", label: "All" },
          { id: "0-2", label: "0-2 yrs" },
          { id: "3-5", label: "3-5 yrs" },
          { id: "6-8", label: "6-8 yrs" },
          { id: "9+", label: "9+ yrs" },
        ].map((p) => {
          const isActive = value === p.id || (p.id === "all" && isAll);
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onChange(p.id)}
              className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-all ${
                isActive
                  ? "bg-violet text-white font-bold shadow-xs"
                  : "bg-secondary/70 text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`}
            >
              {p.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
